'use client';

import LandingLayout from '@/components/LandingLayout';
import { locksmithFaqs } from '@/data/industryFaqs';
import { useAuth } from '@/contexts/AuthContextNext';
import { trackButtonClick, trackPageView, trackPhoneClick } from '@/lib/analytics';
import { trackEvent } from '@/lib/mixpanel';
import {
  CalendarIcon,
  CheckCircleIcon,
  ChevronDownIcon,
  ClockIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  PhoneIcon,
  ShieldCheckIcon,
  SparklesIcon,
  StarIcon,
  XCircleIcon
} from '@heroicons/react/24/outline';
import Link from 'next/link';
import { useEffect, useState } from 'react';

export default function LocksmithLanding() {
  useEffect(() => {
    trackPageView('Locksmith Landing', undefined, { funnel_step: 'industry_landing' });
  }, []);

  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const { isAuthenticated } = useAuth();

  const painPoints = [
    {
      problem: "Emergency lockout calls come at 3 AM and you miss them",
      impact: "$300-800 per missed emergency callout"
    },
    {
      problem: "Answering 'How much for a lockout?' 50+ times daily",
      impact: "8-12 hours weekly on the same questions"
    },
    {
      problem: "Phone ringing while on a job site with another customer",
      impact: "Miss lockouts or have to leave jobs unfinished"
    },
    {
      problem: "Customers calling multiple locksmiths-first to answer wins",
      impact: "Your competitor picks up, you lose the high-margin work"
    }
  ];

  const locksmithBenefits = [
    {
      title: "Never Miss a 3 AM Lockout Call",
      description: "Your AI answers immediately at 3 AM while you sleep. Gets their location and situation, wakes you up. You're first on scene.",
      icon: ExclamationTriangleIcon,
      stat: "100%"
    },
    {
      title: "Save 10-14 Hours Weekly",
      description: "Stop repeating pricing for emergency lockouts, lock changes, and rekeying. AI handles customer intake perfectly every time.",
      icon: ClockIcon,
      stat: "10-14 hrs"
    },
    {
      title: "Instant Appointment Booking",
      description: "Customers schedule lock changes and security consultations directly into your calendar. No back-and-forth calls.",
      icon: CalendarIcon,
      stat: "Auto"
    },
    {
      title: "Qualify Customers Before You Respond",
      description: "AI asks key questions: locked out of car/home/office? Residential or commercial? You get pre-qualified leads only.",
      icon: ShieldCheckIcon,
      stat: "Pre-qualified"
    },
    {
      title: "Handle Volume Spikes (Holidays, Bad Weather)",
      description: "Bad weather brings lockouts. AI scales instantly without hiring extra staff. Turns call surges into revenue surges.",
      icon: SparklesIcon,
      stat: "Unlimited"
    },
    {
      title: "Smart Customer Triage",
      description: "AI identifies emergency lockouts vs. routine service, panic vs. calm customers. You get detailed context before calling back.",
      icon: DocumentTextIcon,
      stat: "Pre-assessed"
    }
  ];

  const scenarios = [
    {
      title: "The 3 AM Lockout You Wish You'd Caught",
      problem: "Christmas morning, 3 AM: Customer locked out of home. Calls you-voicemail. Calls your competitor. Competitor gets $400 service call + happy customer for future work.",
      solution: "With Greet: Your AI answers immediately, assesses the emergency, gets address and details, wakes you up. You're calling them back in 2 minutes. You close the job."
    },
    {
      title: "Friday Night Volume Spike",
      problem: "Friday 5 PM through Sunday: Volume surges 300%. You're handling calls all weekend, burned out, missing half your calls, frustrated customers.",
      solution: "With Greet: Every call answered instantly. Routine questions about pricing and scheduling handled by AI. Emergency lockouts routed to you. You stay sane and close more jobs."
    },
    {
      title: "The Commercial Security Opportunity",
      problem: "Business calls about security upgrades. Your phone goes to voicemail. They call your competitor who is security-focused. You miss a $5,000+ project.",
      solution: "With Greet: AI qualifies the call as commercial security, books an immediate consultation. You call back prepared. 80% close rate on pre-qualified leads."
    }
  ];

  const testimonials = [
    {
      name: "David Martinez",
      business: "Martinez 24/7 Emergency Locksmith",
      stars: 5,
      quote: "I'm a solo operator. Those middle-of-the-night emergency calls are my bread and butter. Greet catches every single 3 AM lockout now. I'm always first to respond, and my emergency revenue is up 60%.",
      metric: "+$2,500/month in emergency work"
    },
    {
      name: "Sarah Thompson",
      business: "Thompson Security Solutions",
      stars: 5,
      quote: "We do high-end commercial security. Greet pre-qualifies those leads perfectly. When I call back, I already know it's a serious prospect. Our commercial closing rate doubled.",
      metric: "50% more commercial jobs"
    },
    {
      name: "James Wilson",
      business: "Elite Locksmith Services",
      stars: 5,
      quote: "Weekends used to be chaos-constant calls, most routine questions. Now Greet handles 70% of the volume. I only get interrupted for real emergencies. Better business AND better personal life.",
      metric: "Weekends back to normal"
    }
  ];

  return (
    <LandingLayout>
      {/* Hero */}
      <section className="relative bg-gradient-to-br from-amber-900 via-slate-900 to-amber-900 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-amber-500/10 via-orange-500/10 to-yellow-500/10"></div>
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[800px] rounded-full bg-gradient-to-r from-amber-500/20 to-yellow-500/20 blur-3xl"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <div className="max-w-4xl">
            <div className="mb-6 inline-flex items-center gap-3 rounded-full bg-amber-500/20 border border-amber-400/30 px-4 py-2 text-sm font-semibold text-amber-100">
              <span className="h-2 w-2 rounded-full bg-amber-400"></span>
              #1 AI Receptionist for Locksmiths
            </div>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight">
              Never Miss Another 3 AM Emergency Lockout
            </h1>
            <p className="text-xl text-amber-100 mb-8 max-w-2xl leading-relaxed">
              Your AI receptionist answers emergency lockouts, qualifies security work, pre-assesses customers, and catches high-margin calls 24/7-while you focus on actually opening locks.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link 
                href={isAuthenticated ? "/app" : "/register"}
                onClick={() => trackButtonClick(isAuthenticated ? "go_to_dashboard" : "start_free_trial", "locksmith_hero")}
                className="bg-amber-500 hover:bg-amber-600 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-colors inline-block"
              >
                {isAuthenticated ? "Go to Dashboard" : "Start Free Trial"}
              </Link>
              <a
                href="tel:+14158773166"
                onClick={() => trackPhoneClick('+14158773166', 'locksmith_demo')}
                className="bg-white/10 border border-white text-white hover:bg-white/20 px-8 py-4 rounded-lg font-semibold text-lg transition-colors inline-flex items-center justify-center"
              >
                <PhoneIcon className="h-5 w-5 mr-2" />
                See Live Demo: (415) 877-3166
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Pain Points Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              The Locksmith's Call Management Crisis
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Emergency lockouts are your highest-paying work. Missing even one call per week costs thousands in lost revenue.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            {painPoints.map((point, index) => (
              <div key={index} className="border-l-4 border-red-500 pl-6 py-4">
                <div className="flex items-start gap-4">
                  <XCircleIcon className="h-6 w-6 text-red-500 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-bold text-gray-900 text-lg mb-2">{point.problem}</h3>
                    <p className="text-red-600 font-semibold">{point.impact}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Built for Locksmiths: Here's What Changes
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Your AI receptionist handles emergencies, qualifies customers, and manages your calendar while you focus on locks and security.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {locksmithBenefits.map((benefit, index) => (
              <div key={index} className="bg-white rounded-2xl border border-gray-100 p-8 hover:shadow-xl transition-all duration-300 hover:-translate-y-2 group">
                <div className="w-16 h-16 rounded-xl bg-gradient-to-r from-amber-500 to-yellow-500 flex items-center justify-center mb-6 shadow-lg transition-transform duration-300 group-hover:scale-110">
                  <benefit.icon className="h-8 w-8 text-white transition-transform duration-300 group-hover:scale-125" />
                </div>
                <div className="inline-block px-4 py-1 bg-amber-100 rounded-full mb-4 transition-all duration-300 group-hover:bg-amber-200 group-hover:scale-105">
                  <span className="text-2xl font-bold text-amber-900 transition-colors duration-300 group-hover:text-amber-900">{benefit.stat}</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3 transition-colors duration-300 group-hover:text-amber-600">{benefit.title}</h3>
                <p className="text-gray-600 leading-relaxed transition-colors duration-300 group-hover:text-gray-700">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Real Scenarios */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              How Locksmiths Actually Use Greet
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Real scenarios. Real money. Real solutions.
            </p>
          </div>
          
          <div className="space-y-8">
            {scenarios.map((scenario, index) => (
              <div key={index} className="bg-gradient-to-r from-amber-50 to-yellow-50 rounded-2xl p-8 border border-amber-200">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{scenario.title}</h3>
                
                <div className="grid md:grid-cols-2 gap-8">
                  <div>
                    <div className="flex items-start gap-3 mb-3">
                      <XCircleIcon className="h-6 w-6 text-red-500 flex-shrink-0 mt-1" />
                      <span className="font-semibold text-gray-900">Without Greet</span>
                    </div>
                    <p className="text-gray-700 leading-relaxed">{scenario.problem}</p>
                  </div>
                  
                  <div>
                    <div className="flex items-start gap-3 mb-3">
                      <CheckCircleIcon className="h-6 w-6 text-green-500 flex-shrink-0 mt-1" />
                      <span className="font-semibold text-gray-900">With Greet</span>
                    </div>
                    <p className="text-gray-700 leading-relaxed">{scenario.solution}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Trusted by Locksmiths
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Real locksmiths. Real results. Real revenue growth.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 group">
                <div className="flex items-center mb-4">
                  {[...Array(testimonial.stars)].map((_, i) => (
                    <StarIcon key={i} className="h-5 w-5 text-yellow-400 fill-current transition-colors duration-300 group-hover:text-yellow-500" />
                  ))}
                </div>
                <p className="text-gray-700 mb-4 leading-relaxed transition-colors duration-300 group-hover:text-gray-900">"{testimonial.quote}"</p>
                <div className="bg-amber-100 rounded-lg px-4 py-3 mb-4 transition-colors duration-300 group-hover:bg-amber-200">
                  <p className="text-amber-900 font-semibold text-sm">{testimonial.metric}</p>
                </div>
                <div>
                  <div className="font-semibold text-gray-900 transition-colors duration-300 group-hover:text-amber-600">{testimonial.name}</div>
                  <div className="text-gray-600 text-sm transition-colors duration-300 group-hover:text-gray-700">{testimonial.business}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Questions from Locksmiths
            </h2>
            <p className="text-xl text-gray-600">
              Everything you need to know about AI call handling for locksmith services.
            </p>
          </div>
          
          <div className="space-y-4">
            {locksmithFaqs.map((faq, index) => (
              <div key={index} className="border border-gray-200 rounded-lg overflow-hidden">
                <button
                  className="w-full px-6 py-4 text-left flex justify-between items-center hover:bg-gray-50 transition-colors"
                  onClick={() => {
                    const isOpening = openFaq !== index;
                    setOpenFaq(isOpening ? index : null);
                    if (isOpening) trackEvent('FAQ Opened', { question: faq.question, page: 'locksmith' });
                  }}
                >
                  <span className="font-semibold text-gray-900">{faq.question}</span>
                  <ChevronDownIcon 
                    className={`h-5 w-5 text-gray-500 transform transition-transform ${
                      openFaq === index ? 'rotate-180' : ''
                    }`} 
                  />
                </button>
                <div className={`px-6 text-gray-600 border-t border-gray-200 bg-gray-50 transition-all duration-300 ${
                  openFaq === index ? 'pb-4 max-h-96' : 'pb-0 max-h-0'
                } overflow-hidden`}>
                  {faq.answer}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-gradient-to-r from-amber-600 to-yellow-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Stop Missing Emergency Lockouts
          </h2>
          <p className="text-xl text-amber-100 mb-8 max-w-2xl mx-auto">
            Your AI receptionist is ready to answer 24/7. Start your free trial today.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              href={isAuthenticated ? "/app" : "/register"}
              onClick={() => trackButtonClick(isAuthenticated ? "go_to_dashboard" : "start_free_trial", "locksmith_cta")}
              className="bg-white text-amber-600 hover:bg-gray-50 px-8 py-4 rounded-lg font-semibold text-lg transition-colors"
            >
              {isAuthenticated ? "Go to Dashboard" : "Start Free Trial"}
            </Link>
            <a
              href="tel:+14158773166"
              onClick={() => trackPhoneClick('+14158773166', 'locksmith_cta')}
              className="bg-transparent border border-white text-white hover:bg-white/10 px-8 py-4 rounded-lg font-semibold text-lg transition-colors inline-flex items-center justify-center"
            >
              <PhoneIcon className="h-5 w-5 mr-2" />
              Call Demo: (415) 877-3166
            </a>
          </div>
        </div>
      </section>
    </LandingLayout>
  );
} 
